import React, { FC, useCallback, useMemo, useState } from 'react';

import {
  Box,
  Text,
  Flex,
  Stack,
  Button,
  Checkbox,
  IconButton,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/hooks';
import { MdChevronRight, MdClose, MdTune } from 'react-icons/md';

import { selectors } from 'store/slices/washhub';

import { useSKU } from 'hooks/useSKU';

import { AnimatePresence, motion } from 'framer-motion';

import { FiltersProps } from './Filters.props';
import { FEE_ADJUSTMENT_TYPES } from 'constants/feeAdjustments';
import { useCollectionPoints } from 'hooks/useCollectionPoints';
import { SKU } from 'types/sku';
import { INVENTORY_CATEGORIES } from 'constants/inventoryCategories';
import { Site } from 'types/site';
import { CollectionPoint } from 'types/location';
import useSiteWithStatus from 'hooks/sites/useSiteWithStatus';
import { Employee, RolesEnum } from 'types/user';
import { rolesMap } from 'components/modules/AddEmployeeModal/AddEmployeeModal.utils';
import { useQuery } from 'react-query';
import { fetchEmployees } from 'api/employees';

const MotionFlex = motion<any>(Flex);

const animationConfig = {
  top: 0,
  left: 2,
  // Above the tables this opens over. The pivot on Inventory Summary has a
  // sticky header and pinned columns of its own, which at zIndex 2 painted
  // straight through the panel and made it look transparent (86bbmcv2b).
  zIndex: 20,
  color: 'primary.500',
  fontSize: 14,
  fontWeight: '600',
  position: 'absolute',
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.3 },
};

const css = {
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    width: '10px',
    height: '10px',
    background: 'primary.900',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'primary.400',
    borderRadius: '24px',
  },
};

const invoiceFilterData = [
  {
    name: 'True',
    value: true,
  },
  {
    name: 'False',
    value: false,
  },
];

const sentToClientFilterData = [
  {
    name: 'True',
    value: true,
  },
  {
    name: 'False',
    value: false,
  },
];

// Fee types are their own filter values (the API matches feeAdjustments.type).
const feeTypeFilterData = FEE_ADJUSTMENT_TYPES.map((type) => ({
  name: type,
  _id: type,
}));

// Left unsorted: the ticket gives these a deliberate order, with Item first.
const categoryFilterData = INVENTORY_CATEGORIES.map((category) => ({
  name: category,
  _id: category,
}));

const allRoles = [
  RolesEnum.WASH_HUB_STAFF,
  RolesEnum.WASH_HUB_ADMIN,
  RolesEnum.SUPER_ADMIN,
];

const FiltersView: FC<FiltersProps> = (props) => {
  const {
    type = 'item',
    isFullButton,
    isSitesOnly,
    isInvoiced,
    invoiced,
    isCollectionPointsOnly,
    isSkuOnly = false,
    venue,
    onApplyFilters,
    onClearFilters,
    isRolesOnly,
    isEmployeesOnly,
    employees,
    isAccountManagersOnly,
    roles,
    isSentToClientOnly,
    sentToClient,
    isFeeTypesOnly,
    feeAdjustmentTypes,
    isCategoriesOnly = false,
    categories,
  } = props;
  const washHubId = selectors.useSelectedWashHubId();
  const { data: sitesData } = useSiteWithStatus('active');
  const { data: inActiveSitesData } = useSiteWithStatus('in-active');
  const { data: archivedSitesData } = useSiteWithStatus('archived');
  // Equipment SKUs aren't tied to a hub: the hub filter would return none.
  const { data: skusData } = useSKU(
    type,
    washHubId,
    undefined,
    undefined,
    type === 'equipment',
  );

  // /sku/list takes a single type, so equipment needs its own call. It also has
  // to skip the wash hub filter: that query matches equipment against the hub's
  // packages array, which equipment SKUs are never part of, so passing a hub
  // would return nothing.
  const { data: equipmentSkusData } = useSKU(
    'equipment',
    washHubId,
    undefined,
    undefined,
    true,
  );

  const skuFilterData = useMemo(
    () => [
      ...(skusData || []),
      ...(isCategoriesOnly ? equipmentSkusData || [] : []),
    ],
    [skusData, equipmentSkusData, isCategoriesOnly],
  );
  const queryKey = [
    `fetch-employees-${washHubId}`,
    washHubId,
    isAccountManagersOnly,
  ];

  const queryFn = () =>
    fetchEmployees({
      washHub: washHubId,
      limit: 1000,
      ...(isAccountManagersOnly ? { getAll: true } : {}),
    });

  const _options = {
    keepPreviousData: true,
  };

  const { data: employeesData } = useQuery(queryKey, queryFn, _options);

  const employeesArray = (employeesData?.items || []).map((item: Employee) => ({
    name: item.fname + ' ' + item.lname,
    _id: item._id,
  }));
  const accountManagersArray = (employeesData?.items || []).map(
    (item: Employee) => ({
      name: item.fname + ' ' + item.lname,
      _id: item._id,
    }),
  );

  const rolesArray = allRoles.map((item: string) => ({
    name: rolesMap[item],
    _id: item,
  }));

  const { data: collectionPointsData } = useCollectionPoints(); //bring only the collection points not suspended.

  const { isOpen, onClose, onToggle } = useDisclosure();

  const sortData = (data: any) => {
    return data?.sort((a: { name: string }, b: { name: string }) =>
      a.name
        .toLowerCase()
        .localeCompare(b.name.toLowerCase(), 'en', { numeric: true }),
    );
  };

  const dataToFilter = () => {
    if (isSkuOnly) {
      return [
        { data: sortData(skuFilterData), name: 'Skus', type: 'sku' },
        ...(isCategoriesOnly
          ? [{ data: categoryFilterData, name: 'Categories', type: 'category' }]
          : []),
      ];
    }
    if (
      !isSitesOnly &&
      !isCollectionPointsOnly &&
      !isRolesOnly &&
      !isEmployeesOnly
    ) {
      return [
        { data: sortData(sitesData), name: 'Active Sites', type: 'site' },
        ...(inActiveSitesData && inActiveSitesData.length > 0
          ? [
              {
                data: sortData(inActiveSitesData),
                name: 'Inactive Sites',
                type: 'site',
              },
            ]
          : []),
        ...(archivedSitesData && archivedSitesData.length > 0
          ? [
              {
                data: sortData(archivedSitesData),
                name: 'Archived Sites',
                type: 'site',
              },
            ]
          : []),
        { data: sortData(skusData), name: 'Skus', type: 'sku' },
      ];
    }
    if (isSitesOnly) {
      return [
        { data: sortData(sitesData), name: 'Active Sites', type: 'site' },
        ...(inActiveSitesData && inActiveSitesData.length > 0
          ? [
              {
                data: sortData(inActiveSitesData),
                name: 'Inactive Sites',
                type: 'site',
              },
            ]
          : []),
        ...(archivedSitesData && archivedSitesData.length > 0
          ? [
              {
                data: sortData(archivedSitesData),
                name: 'Archived Sites',
                type: 'site',
              },
            ]
          : []),
        ...(isInvoiced
          ? [
              {
                data: sortData(invoiceFilterData),
                name: 'Invoice',
                type: 'invoice',
              },
            ]
          : []),
        ...(isAccountManagersOnly
          ? [
              {
                data: sortData(accountManagersArray),
                name: 'Account Managers',
                type: 'accountManagers',
              },
            ]
          : []),
        ...(isSentToClientOnly
          ? [
              {
                data: sortData(sentToClientFilterData),
                name: 'Sent To Client',
                type: 'sentToClient',
              },
            ]
          : []),
        ...(isFeeTypesOnly
          ? [
              {
                data: feeTypeFilterData,
                name: 'Fee/Adjustments',
                type: 'feeAdjustmentTypes',
              },
            ]
          : []),
      ];
    }
    if (isRolesOnly) {
      return [
        { data: sortData(rolesArray), name: 'Roles', type: 'roles' },
        {
          data: sortData(employeesArray),
          name: 'Employees',
          type: 'employee',
        },
      ];
    }
    if (isCollectionPointsOnly) {
      return [
        {
          data: sortData(collectionPointsData),
          name: 'Collection Points',
          type: 'collectionPoints',
        },
      ];
    }
  };
  const dataTobeFiltered = dataToFilter();
  const [selected, setSelected] = useState();

  const [localSelectedData, setLocalSelectedData] = useState<string[]>(
    venue || [],
  );
  const [localSelectedEmployees, setLocalSelectedEmployees] = useState<
    string[]
  >(employees || []);
  const [
    localSelectedAccountManagers,
    setLocalSelectedAccountManagers,
  ] = useState<string[]>([]);
  const [localSelectedRoles, setLocalSelectedRoles] = useState<string[]>(
    roles || [],
  );
  const [localSelectedSentToClient, setLocalSelectedSentToClient] = useState<
    boolean | undefined
  >(sentToClient);
  const [localSelectedFeeTypes, setLocalSelectedFeeTypes] = useState<string[]>(
    feeAdjustmentTypes || [],
  );
  // Categories keep their own state rather than sharing localSelectedData with
  // Skus: that state is a single list, so the two sections would overwrite each
  // other's selections and only the last one clicked would ever be applied.
  const [localSelectedCategories, setLocalSelectedCategories] = useState<
    string[]
  >(categories || []);

  const isBoolean = (value: boolean | undefined) => {
    return typeof value === 'boolean';
  };

  const [localSelectedInvoiced, setLocalSelectedInvoiced] = useState<
    boolean | undefined
  >(invoiced);
  const [index, setIndex] = useState(0);

  const [selectedData, setSelectedData] = useState<string[]>(venue || []);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>(
    employees || [],
  );
  const [selectedAccountManagers, setSelectedAccountManagers] = useState<
    string[]
  >([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(roles || []);
  const [selectedInvoiced, setSelectedInvoiced] = useState<boolean | undefined>(
    invoiced,
  );
  const [selectedSentToClient, setSelectedSentToClient] = useState<
    boolean | undefined
  >(sentToClient);
  const [selectedFeeTypes, setSelectedFeeTypes] = useState<string[]>(
    feeAdjustmentTypes || [],
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    categories || [],
  );

  const isFilters = useMemo(
    () =>
      Boolean(selectedData?.length) ||
      isBoolean(selectedInvoiced) ||
      Boolean(selectedEmployees?.length) ||
      Boolean(selectedRoles?.length) ||
      Boolean(selectedAccountManagers?.length) ||
      isBoolean(selectedSentToClient) ||
      Boolean(selectedFeeTypes?.length) ||
      Boolean(selectedCategories?.length),
    [
      selectedData.length,
      selectedInvoiced,
      selectedEmployees.length,
      selectedRoles.length,
      selectedAccountManagers.length,
      selectedSentToClient,
      selectedFeeTypes.length,
      selectedCategories.length,
    ],
  );

  const onCancel = () => {
    onClose();

    setLocalSelectedData(selectedData);
    setLocalSelectedEmployees(selectedEmployees);
    setLocalSelectedRoles(selectedRoles);
    setLocalSelectedAccountManagers(selectedAccountManagers);
    setLocalSelectedSentToClient(selectedSentToClient);
    setLocalSelectedFeeTypes(selectedFeeTypes);
    setLocalSelectedCategories(selectedCategories);
  };

  const onApply = () => {
    setSelectedData(localSelectedData);
    setSelectedEmployees(localSelectedEmployees);
    setSelectedAccountManagers(localSelectedAccountManagers);
    setSelectedInvoiced(localSelectedInvoiced);
    setSelectedRoles(localSelectedRoles);
    setSelectedSentToClient(localSelectedSentToClient);
    setSelectedFeeTypes(localSelectedFeeTypes);
    setSelectedCategories(localSelectedCategories);

    if (
      localSelectedData.length > 0 &&
      selected &&
      ![
        'employee',
        'roles',
        'accountManagers',
        'invoice',
        'sentToClient',
        'feeAdjustmentTypes',
        'category',
      ].includes(selected)
    ) {
      onApplyFilters(localSelectedData, selected);
    }

    if (localSelectedEmployees.length > 0) {
      onApplyFilters(localSelectedEmployees, 'employee');
    }
    if (localSelectedAccountManagers.length > 0) {
      onApplyFilters(localSelectedAccountManagers, 'accountManagers');
    }
    if (localSelectedRoles.length > 0) {
      onApplyFilters(localSelectedRoles, 'roles');
    }
    if (typeof localSelectedInvoiced === 'boolean') {
      onApplyFilters(localSelectedInvoiced, 'invoice');
    }
    if (typeof localSelectedSentToClient === 'boolean') {
      onApplyFilters(localSelectedSentToClient, 'sentToClient');
    }
    if (localSelectedFeeTypes.length > 0) {
      onApplyFilters(localSelectedFeeTypes, 'feeAdjustmentTypes');
    }
    // Dispatched even when empty, unlike the sections above: clearing every
    // category has to actually clear the filter rather than leave the last
    // applied value in place.
    if (isCategoriesOnly) {
      onApplyFilters(localSelectedCategories, 'category');
    }

    onClose();
  };

  const onClear = () => {
    setSelectedData([]);
    setSelectedInvoiced(undefined);
    setLocalSelectedData([]);
    setLocalSelectedRoles([]);
    setSelectedRoles([]);
    setLocalSelectedEmployees([]);
    setSelectedEmployees([]);
    setLocalSelectedAccountManagers([]);
    setSelectedAccountManagers([]);
    setLocalSelectedInvoiced(undefined);
    setLocalSelectedSentToClient(undefined);
    setSelectedSentToClient(undefined);
    setLocalSelectedFeeTypes([]);
    setSelectedFeeTypes([]);
    setLocalSelectedCategories([]);
    setSelectedCategories([]);
    onClearFilters();
  };

  const renderButton = useCallback(() => {
    if (isFullButton) {
      return (
        <Button
          px={3}
          mb={5}
          h="40px"
          w="150px"
          bg="surface.300"
          variant="ghost"
          onClick={onToggle}
          justifyContent="space-between"
          rightIcon={<MdTune size="24px" />}>
          Filters
        </Button>
      );
    }

    return (
      <IconButton
        mb={5}
        w="50px"
        h="50px"
        bg="surface.300"
        variant="ghost"
        onClick={onToggle}
        aria-label="filter-button"
        icon={<MdTune size="24px" />}
      />
    );
  }, [isOpen]);

  const renderItems = (index: number, _selectedData: string[], data: any[]) => {
    return (
      <>
        {data &&
          data[index] &&
          data[index].data?.map((item: any, idx: number) => {
            const isInvoiceChecked = localSelectedInvoiced === item?.value;
            const isChecked = _selectedData.includes(item?._id);
            const isCheckedEmployee = localSelectedEmployees.includes(
              item?._id,
            );
            const isCheckedRole = localSelectedRoles.includes(item?._id);
            const isSentToClientChecked =
              localSelectedSentToClient === item?.value;

            if (data[index].type === 'invoice') {
              return (
                <Checkbox
                  key={`${idx}-${item.value}`}
                  isChecked={isInvoiceChecked}
                  onChange={() => {
                    if (localSelectedInvoiced === item.value) {
                      setLocalSelectedInvoiced(undefined);
                    } else {
                      setLocalSelectedInvoiced(item.value);
                    }
                  }}
                  mb={2}
                  w="100%"
                  colorScheme="primary">
                  <Stack minW="100%" isInline>
                    <Text flex={1} w="160px" noOfLines={1}>
                      {item.name}
                    </Text>
                  </Stack>
                </Checkbox>
              );
            }
            if (data[index].type === 'sentToClient') {
              return (
                <Checkbox
                  key={`${idx}-${item.value}`}
                  isChecked={isSentToClientChecked}
                  onChange={() => {
                    if (localSelectedSentToClient === item.value) {
                      setLocalSelectedSentToClient(undefined);
                    } else {
                      setLocalSelectedSentToClient(item.value);
                    }
                  }}
                  mb={2}
                  w="100%"
                  colorScheme="primary">
                  <Stack minW="100%" isInline>
                    <Text flex={1} w="160px" noOfLines={1}>
                      {item.name}
                    </Text>
                  </Stack>
                </Checkbox>
              );
            }
            if (data[index].type === 'feeAdjustmentTypes') {
              const isCheckedFeeType = localSelectedFeeTypes.includes(
                item?._id,
              );
              return (
                <Checkbox
                  key={`${idx}-${item._id}`}
                  isChecked={isCheckedFeeType}
                  onChange={() => {
                    if (isCheckedFeeType) {
                      setLocalSelectedFeeTypes((prev) =>
                        prev.filter((s) => s !== item._id),
                      );
                    } else {
                      setLocalSelectedFeeTypes((prev) => [...prev, item._id]);
                    }
                  }}
                  mb={2}
                  w="100%"
                  colorScheme="primary">
                  <Stack minW="100%" isInline>
                    <Text flex={1} w="160px" noOfLines={1}>
                      {item.name}
                    </Text>
                  </Stack>
                </Checkbox>
              );
            }
            if (data[index].type === 'category') {
              const isCheckedCategory = localSelectedCategories.includes(
                item?._id,
              );
              return (
                <Checkbox
                  key={`${idx}-${item._id}`}
                  isChecked={isCheckedCategory}
                  onChange={() => {
                    if (isCheckedCategory) {
                      setLocalSelectedCategories((prev) =>
                        prev.filter((s) => s !== item._id),
                      );
                    } else {
                      setLocalSelectedCategories((prev) => [...prev, item._id]);
                    }
                  }}
                  mb={2}
                  w="100%"
                  colorScheme="primary">
                  <Stack minW="100%" isInline>
                    <Text flex={1} w="160px" noOfLines={1}>
                      {item.name}
                    </Text>
                  </Stack>
                </Checkbox>
              );
            }
            if (data[index].type === 'employee') {
              return (
                <Checkbox
                  key={`${idx}-${item.value}`}
                  isChecked={isCheckedEmployee}
                  onChange={() => {
                    if (isCheckedEmployee) {
                      setLocalSelectedEmployees((prev) =>
                        prev.filter((s) => s !== item._id),
                      );
                    } else {
                      setLocalSelectedEmployees((prev) => [...prev, item._id]);
                    }
                  }}
                  mb={2}
                  w="100%"
                  colorScheme="primary">
                  <Stack minW="100%" isInline>
                    <Text flex={1} w="160px" noOfLines={1}>
                      {item.name}
                    </Text>
                  </Stack>
                </Checkbox>
              );
            }
            if (data[index].type === 'accountManagers') {
              const isCheckedAccountManager = localSelectedAccountManagers.includes(
                item?._id,
              );
              return (
                <Checkbox
                  key={`${idx}-${item.value}`}
                  isChecked={isCheckedAccountManager}
                  onChange={() => {
                    if (isCheckedAccountManager) {
                      setLocalSelectedAccountManagers((prev) =>
                        prev.filter((s) => s !== item._id),
                      );
                    } else {
                      setLocalSelectedAccountManagers((prev) => [
                        ...prev,
                        item._id,
                      ]);
                    }
                  }}
                  mb={2}
                  w="100%"
                  colorScheme="primary">
                  <Stack minW="100%" isInline>
                    <Text flex={1} w="160px" noOfLines={1}>
                      {item.name}
                    </Text>
                  </Stack>
                </Checkbox>
              );
            }
            if (data[index].type === 'roles') {
              return (
                <Checkbox
                  key={`${idx}-${item.value}`}
                  isChecked={isCheckedRole}
                  onChange={() => {
                    if (isCheckedRole) {
                      setLocalSelectedRoles((prev) =>
                        prev.filter((s) => s !== item._id),
                      );
                    } else {
                      setLocalSelectedRoles((prev) => [...prev, item._id]);
                    }
                  }}
                  mb={2}
                  w="100%"
                  colorScheme="primary">
                  <Stack minW="100%" isInline>
                    <Text flex={1} w="160px" noOfLines={1}>
                      {item.name}
                    </Text>
                  </Stack>
                </Checkbox>
              );
            }

            return (
              <Checkbox
                isChecked={isChecked}
                onChange={() => {
                  if (isChecked) {
                    setLocalSelectedData((prev) =>
                      prev.filter((s) => s !== item._id),
                    );
                  } else {
                    setLocalSelectedData((prev) => [...prev, item._id]);
                  }
                }}
                mb={2}
                w="100%"
                key={`${idx}-${item._id}`}
                colorScheme="primary">
                <Stack minW="100%" isInline>
                  <Text flex={1} w="160px" noOfLines={1}>
                    {item.name}
                  </Text>
                </Stack>
              </Checkbox>
            );
          })}
      </>
    );
  };

  const renderFiltersOptions = () => {
    return (
      <>
        {dataTobeFiltered?.map(
          (filter: SKU[] | Site[] | CollectionPoint[] | any, idx) => {
            return (
              <Flex
                key={`${idx}-${filter.data?._id}`}
                cursor="pointer"
                px={4}
                py={2.5}
                maxW="350px"
                bg={selected && idx === index ? 'surface.400' : '#fff'}
                justify="space-between"
                align="center"
                onClick={() => {
                  setIndex(idx);
                  setSelected(filter.type);
                }}>
                <Text>{filter.name}</Text>
                <MdChevronRight size="20px" />
              </Flex>
            );
          },
        )}
      </>
    );
  };

  return (
    <Flex userSelect="none">
      {renderButton()}
      <Box position="relative">
        <AnimatePresence>
          {isOpen && (
            <MotionFlex {...animationConfig}>
              <Flex
                bg="#fff"
                direction="column"
                borderRadius={8}
                overflow="hidden"
                boxShadow="0px 0px 5px 0px rgba(0,0,0,0.1)">
                {renderFiltersOptions()}
              </Flex>
              {selected && (
                <Flex
                  bg="#fff"
                  top={0}
                  right={0}
                  zIndex={1}
                  minW="240px"
                  marginLeft={2}
                  borderRadius={8}
                  align="flex-start"
                  direction="column"
                  boxShadow="0px 0px 5px 0px rgba(0,0,0,0.1)"
                  maxH="350px">
                  <Flex
                    py={2}
                    px={3}
                    direction="column"
                    overflow="auto"
                    css={css}>
                    {dataTobeFiltered &&
                      renderItems(index, localSelectedData, dataTobeFiltered)}
                  </Flex>
                  <Stack alignSelf="center" py={2} px={3} isInline>
                    <Button onClick={onCancel} size="sm" variant="outline">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={onApply}>
                      Apply
                    </Button>
                  </Stack>
                </Flex>
              )}
            </MotionFlex>
          )}
        </AnimatePresence>
      </Box>
      {isFilters && (
        <Button
          bg="surface.300"
          variant="ghost"
          h="40px"
          ml={2}
          rightIcon={<MdClose />}
          onClick={onClear}>
          Clear
        </Button>
      )}
    </Flex>
  );
};

export default FiltersView;
